import React, { useState } from 'react'
import { Code, Github, ExternalLink, Star, Users, TrendingUp, Shield, Zap, Award, Calendar, MessageCircle, Bookmark, Filter } from 'lucide-react'

interface BlockchainDeveloperProps {
  data: {
    developerName: string
    title: string
    bio: string
    avatar: string
    coverImage: string
    location: string
    experience: string
    verified: boolean
    socialLinks: {
      github: string
      twitter: string
      linkedin: string
      website: string
    }
    skills: Array<{
      name: string
      level: number
      category: 'smart-contracts' | 'defi' | 'nft' | 'infrastructure' | 'security'
    }>
    projects: Array<{
      id: string
      name: string
      description: string
      technology: string
      category: 'defi' | 'nft' | 'dao' | 'infrastructure' | 'game'
      githubUrl: string
      liveUrl?: string
      stars: number
      forks: number
      language: string
      lastUpdated: string
      featured: boolean
      status: 'completed' | 'in-development' | 'maintenance'
    }>
    contributions: Array<{
      id: string
      repository: string
      type: 'pull-request' | 'issue' | 'commit'
      title: string
      description: string
      date: string
      status: 'merged' | 'open' | 'closed'
      impact: 'high' | 'medium' | 'low'
    }>
    audits: Array<{
      id: string
      project: string
      type: 'smart-contract' | 'defi-protocol' | 'nft-contract'
      findings: number
      critical: number
      high: number
      medium: number
      low: number
      status: 'completed' | 'in-progress' | 'scheduled'
      date: string
    }>
    certifications: Array<{
      id: string
      name: string
      issuer: string
      date: string
      credential: string
      icon: string
    }>
    blogPosts: Array<{
      id: string
      title: string
      excerpt: string
      category: 'tutorial' | 'analysis' | 'opinion' | 'news'
      readTime: string
      publishedAt: string
      views: number
      likes: number
      featured: boolean
    }>
    stats: {
      totalCommits: number
      repositories: number
      pullRequests: number
      issuesResolved: number
      codeReviews: number
      linesOfCode: number
    }
  }
}

export default function BlockchainDeveloper({ data }: BlockchainDeveloperProps) {
  const [selectedTab, setSelectedTab] = useState<'projects' | 'contributions' | 'audits' | 'blog'>('projects')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set())
  const [bookmarkedPosts, setBookmarkedPosts] = useState<Set<string>>(new Set())

  const toggleLike = (postId: string) => {
    const newLikedPosts = new Set(likedPosts)
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId)
    } else {
      newLikedPosts.add(postId)
    }
    setLikedPosts(newLikedPosts)
  }

  const toggleBookmark = (postId: string) => {
    const newBookmarkedPosts = new Set(bookmarkedPosts)
    if (newBookmarkedPosts.has(postId)) {
      newBookmarkedPosts.delete(postId)
    } else {
      newBookmarkedPosts.add(postId)
    }
    setBookmarkedPosts(newBookmarkedPosts)
  }

  const getSkillCategoryColor = (category: string) => {
    switch (category) {
      case 'smart-contracts': return 'bg-blue-500/20 text-blue-400'
      case 'defi': return 'bg-green-500/20 text-green-400'
      case 'nft': return 'bg-purple-500/20 text-purple-400'
      case 'infrastructure': return 'bg-orange-500/20 text-orange-400'
      case 'security': return 'bg-red-500/20 text-red-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getProjectCategoryColor = (category: string) => {
    switch (category) {
      case 'defi': return 'bg-green-500/20 text-green-400'
      case 'nft': return 'bg-purple-500/20 text-purple-400'
      case 'dao': return 'bg-blue-500/20 text-blue-400'
      case 'infrastructure': return 'bg-orange-500/20 text-orange-400'
      case 'game': return 'bg-pink-500/20 text-pink-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400'
      case 'in-development': return 'bg-blue-500/20 text-blue-400'
      case 'maintenance': return 'bg-yellow-500/20 text-yellow-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'bg-red-500/20 text-red-400'
      case 'medium': return 'bg-yellow-500/20 text-yellow-400'
      case 'low': return 'bg-green-500/20 text-green-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const filteredProjects = selectedCategory === 'all' 
    ? data.projects 
    : data.projects.filter(project => project.category === selectedCategory)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-blue-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Code className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {data.developerName}
                </h1>
                <p className="text-blue-200 text-sm">{data.title}</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => setSelectedTab('projects')}
                className={`transition-colors ${selectedTab === 'projects' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Projects
              </button>
              <button
                onClick={() => setSelectedTab('contributions')}
                className={`transition-colors ${selectedTab === 'contributions' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Contributions
              </button>
              <button
                onClick={() => setSelectedTab('audits')}
                className={`transition-colors ${selectedTab === 'audits' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Audits
              </button>
              <button
                onClick={() => setSelectedTab('blog')}
                className={`transition-colors ${selectedTab === 'blog' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Blog
              </button>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                Contact
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Hero */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Profile Info */}
            <div className="lg:w-1/3">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Code className="w-12 h-12 text-white" />
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h2 
                      className="text-2xl font-bold"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {data.developerName}
                    </h2>
                    {data.verified && (
                      <div className="bg-gradient-to-r from-gold-400 to-gold-600 rounded-full p-1">
                        <Shield className="w-4 h-4 text-white fill-current" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-blue-300 text-lg">{data.title}</p>
                  <p className="text-white/80 mt-4">{data.bio}</p>
                  
                  <div className="flex items-center justify-center gap-4 mt-4 text-sm text-white/60">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {data.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      {data.experience}
                    </span>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex justify-center gap-4 mb-6">
                  {Object.entries(data.socialLinks).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-colors"
                    >
                      {platform === 'github' && <Github className="w-5 h-5 text-white" />}
                      {platform === 'twitter' && <span className="text-xs text-white">Twitter</span>}
                      {platform === 'linkedin' && <span className="text-xs text-white">LinkedIn</span>}
                      {platform === 'website' && <ExternalLink className="w-5 h-5 text-white" />}
                    </a>
                  ))}
                </div>

                {/* Stats */}
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-white/80">Repositories</span>
                    <span className="text-white font-semibold">{data.stats.repositories}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Total Commits</span>
                    <span className="text-white font-semibold">{data.stats.totalCommits.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Pull Requests</span>
                    <span className="text-white font-semibold">{data.stats.pullRequests}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Issues Resolved</span>
                    <span className="text-white font-semibold">{data.stats.issuesResolved}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Skills & Stats */}
            <div className="lg:w-2/3">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 mb-8">
                <h3 
                  className="text-2xl font-bold mb-6"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Technical Skills
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {data.skills.map((skill, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">{skill.name}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getSkillCategoryColor(skill.category)}`}>
                          {skill.category.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${skill.level}%` }}
                        ></div>
                      </div>
                      <div className="text-sm text-white/60">{skill.level}%</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-4 border border-blue-400/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Github className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 text-sm">Commits</span>
                  </div>
                  <div className="text-xl font-bold text-white">{data.stats.totalCommits.toLocaleString()}</div>
                </div>
                
                <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-4 border border-green-400/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Code className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm">Repos</span>
                  </div>
                  <div className="text-xl font-bold text-white">{data.stats.repositories}</div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-4 border border-purple-400/30">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageCircle className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-400 text-sm">PRs</span>
                  </div>
                  <div className="text-xl font-bold text-white">{data.stats.pullRequests}</div>
                </div>
                
                <div className="bg-gradient-to-r from-orange-500/20 to-orange-600/20 backdrop-blur-sm rounded-xl p-4 border border-orange-400/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-4 h-4 text-orange-400" />
                    <span className="text-orange-400 text-sm">Reviews</span>
                  </div>
                  <div className="text-xl font-bold text-white">{data.stats.codeReviews}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Projects Tab */}
          {selectedTab === 'projects' && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <h2 
                  className="text-3xl font-bold"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Open Source Projects
                </h2>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-blue-400"
                >
                  <option value="all">All Categories</option>
                  <option value="defi">DeFi</option>
                  <option value="nft">NFT</option>
                  <option value="dao">DAO</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="game">Gaming</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getProjectCategoryColor(project.category)}`}>
                          {project.category.toUpperCase()}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(project.status)}`}>
                          {project.status.replace('-', ' ')}
                        </span>
                        {project.featured && (
                          <span className="bg-gold-500/20 text-gold-400 px-2 py-1 rounded text-xs font-semibold">
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3">{project.name}</h3>
                    <p className="text-white/80 mb-4">{project.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-white/60 mb-4">
                      <span className="flex items-center gap-1">
                        <Code className="w-4 h-4" />
                        {project.technology}
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {project.stars}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {project.forks}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Github className="w-4 h-4" />
                          Code
                        </a>
                        {project.liveUrl && (
                          <a
                            href={project.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Live Demo
                          </a>
                        )}
                      </div>
                      <span className="text-white/60 text-sm">{project.lastUpdated}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contributions Tab */}
          {selectedTab === 'contributions' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Open Source Contributions
              </h2>
              
              <div className="space-y-6">
                {data.contributions.map((contribution) => (
                  <div
                    key={contribution.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          contribution.type === 'pull-request' ? 'bg-green-500/20 text-green-400' :
                          contribution.type === 'issue' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {contribution.type === 'pull-request' && <TrendingUp className="w-5 h-5" />}
                          {contribution.type === 'issue' && <MessageCircle className="w-5 h-5" />}
                          {contribution.type === 'commit' && <Code className="w-5 h-5" />}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-white">{contribution.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getImpactColor(contribution.impact)}`}>
                            {contribution.impact} impact
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            contribution.status === 'merged' ? 'bg-green-500/20 text-green-400' :
                            contribution.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {contribution.status}
                          </span>
                        </div>
                        
                        <p className="text-white/80 mb-3">{contribution.description}</p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-blue-300 text-sm">{contribution.repository}</span>
                          <span className="text-white/60 text-sm">{contribution.date}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audits Tab */}
          {selectedTab === 'audits' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Security Audits
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.audits.map((audit) => (
                  <div
                    key={audit.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white">{audit.project}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(audit.status)}`}>
                        {audit.status}
                      </span>
                    </div>
                    
                    <p className="text-white/80 mb-4 capitalize">{audit.type.replace('-', ' ')}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between">
                        <span className="text-white/60">Total Findings</span>
                        <span className="text-white font-semibold">{audit.findings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-400">Critical</span>
                        <span className="text-red-400 font-semibold">{audit.critical}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-400">High</span>
                        <span className="text-orange-400 font-semibold">{audit.high}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-400">Medium</span>
                        <span className="text-yellow-400 font-semibold">{audit.medium}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-400">Low</span>
                        <span className="text-green-400 font-semibold">{audit.low}</span>
                      </div>
                    </div>
                    
                    <div className="text-white/60 text-sm">{audit.date}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blog Tab */}
          {selectedTab === 'blog' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Technical Blog
              </h2>
              
              <div className="space-y-6">
                {data.blogPosts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          post.category === 'tutorial' ? 'bg-blue-500/20 text-blue-400' :
                          post.category === 'analysis' ? 'bg-green-500/20 text-green-400' :
                          post.category === 'opinion' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                        </span>
                        {post.featured && (
                          <span className="bg-gold-500/20 text-gold-400 px-2 py-1 rounded text-xs font-semibold">
                            Featured
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleBookmark(post.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            bookmarkedPosts.has(post.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          <Bookmark className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-3">{post.title}</h3>
                    <p className="text-white/80 mb-4">{post.excerpt}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-white/60">
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {post.publishedAt}
                        </span>
                        <span className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          {post.readTime}
                        </span>
                        <span className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {post.views.toLocaleString()}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => toggleLike(post.id)}
                        className={`flex items-center gap-2 transition-colors ${
                          likedPosts.has(post.id) ? 'text-red-400' : 'text-white/60 hover:text-red-400'
                        }`}
                      >
                        <Star className={`w-4 h-4 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                        {post.likes + (likedPosts.has(post.id) ? 1 : 0)}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">GitHub</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">LinkedIn</a>
            <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">Website</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 {data.developerName}. Building the future of blockchain technology.
          </p>
        </div>
      </footer>
    </div>
  )
}
